import React, { useState, useEffect } from 'react';
import { ProfileProps } from './types'
import useAsyncEffect from 'use-async-effect';
import { apiGatewayUrlParam, publicContentUrl } from '../../config/ServiceConfig';
import ApiService from '../../service/ApiService';
import Paper from '@mui/material/Paper';
import { Theme, createStyles } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Link from '@mui/material/Link';
import { Link as RouterLink } from 'react-router-dom';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import SetProfilePhotoModal from '../../components/editProfile/setProfilePhotoModal';
import EditContactInfo from '../../components/editProfile/editContact';
import { NetworkUsername } from '../../components/editProfile/editContact/types';
import { useAuth, useModal, useAvatar } from '../../hooks';

interface MyStyles {
    profilePhotoPaper: string;
    profileAvatar: string;
    cameraAvatar: string;
    userName: string;
    socialMediaSelect: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    profilePhotoPaper: {
        marginTop: theme.spacing(4),
        textAlign: "center",
        maxWidth: theme.breakpoints.values["md"],
        margin: "auto"
    },
    profileAvatar: {
        maxWidth: "300px",
        maxHeight: "300px",
        width: "75vw",
        height: "75vw"
    },
    cameraAvatar: {
        width: theme.spacing(5),
        height: theme.spacing(5),
        backgroundColor: "rgb(220,220,220)",
        borderColor: theme.palette.background.paper,
        borderWidth: "4px",
        borderStyle: "solid"
    },
    userName: {
        wordWrap: "break-word",
        textAlign: "center",
        [theme.breakpoints.up('md')]: {
            maxWidth: "100%",
        },
        [theme.breakpoints.down('sm')]: {
            maxWidth: "90vw",
        },
    },
    socialMediaSelect: {
        width: "100px"
    }
  }),
);

enum ProfilePhotoState {
    LoadingAuth,
    NotSignedIn,
    NotSet,
    Set
}

const apiService = new ApiService(apiGatewayUrlParam);

const Profile: React.FunctionComponent<ProfileProps> = (props) => {
    const classes = useStyles() as MyStyles;
    
    // Use our new state management hooks
    const { isAuthenticated, isLoading, user } = useAuth();
    const { handle, isLoaded: avatarLoaded } = useAvatar();
    const setProfilePhotoModal = useModal('setProfilePhoto');

    // Local state for profile-specific data
    const [profilePhotoState, setProfilePhotoState] = useState(ProfilePhotoState.LoadingAuth);
    const [profilePhotId, setProfilePhotoId] = useState("");
    const [contactInfo, setContactInfo] = useState([] as Array<NetworkUsername>);
    const [profileLoaded, setProfileLoaded] = useState(false);

    // Update profile photo state based on auth status
    useEffect(() => {
        if (isLoading) {
            setProfilePhotoState(ProfilePhotoState.LoadingAuth);
        } else if (!isAuthenticated) {
            setProfilePhotoState(ProfilePhotoState.NotSignedIn);
        }
        // ProfilePhotoState.Set/NotSet will be determined by the API call
    }, [isAuthenticated, isLoading]);

    useAsyncEffect(async isMounted => {
        if (!user) {
            return;
        }

        // Fetch profile data and avatar data in parallel
        const [profileData, avatarData] = await Promise.all([
            apiService.getRequest(`users/${user.username}/profile`),
            apiService.getUserAvatar(user.username)
        ]);
        
        console.log("profileData = " + JSON.stringify(profileData));
        console.log("avatarData = " + JSON.stringify(avatarData));

        if (!isMounted()) return;
        
        // Set profile photo state
        if (profileData?.profilePhotoId) {
            setProfilePhotoId(profileData.profilePhotoId);
            setProfilePhotoState(ProfilePhotoState.Set);
        } else {
            setProfilePhotoState(ProfilePhotoState.NotSet);
        }

        // Set contact info
        if (profileData?.contactInfo) {
            setContactInfo(profileData.contactInfo);
        }

        setProfileLoaded(true);
    }, [user]);
    
    // Render loading state
    if (isLoading) {
        return (
            <Grid container justifyContent="center" style={{ marginTop: '2rem' }}>
                <CircularProgress />
            </Grid>
        );
    }

    // Render not signed in state
    if (!isAuthenticated) {
        return (
            <Grid container justifyContent="center" style={{ marginTop: '2rem' }}>
                <Typography variant="h6">
                    You must first sign in to edit your profile.
                </Typography>
            </Grid>
        );
    }

    // At this point, user is guaranteed to be non-null due to auth checks above
    const authenticatedUser = user!;
    
    let profilePhotoComponent;
    switch (profilePhotoState) {
        case ProfilePhotoState.Set:
        case ProfilePhotoState.NotSet:
            profilePhotoComponent = (
                <Badge
                    overlap="circular"
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right"
                    }}
                    badgeContent={(
                        <Avatar className={classes.cameraAvatar}>
                            <IconButton
                                onClick={setProfilePhotoModal.open}
                            >
                                <PhotoCameraIcon/>
                            </IconButton>
                        </Avatar>
                    )}
                >
                    <Avatar
                        className={classes.profileAvatar}
                        src={`${publicContentUrl}users/${authenticatedUser.username}/${profilePhotId}`}
                    >
                    </Avatar>
                </Badge>
            );
            break;
        case ProfilePhotoState.LoadingAuth:
            profilePhotoComponent = <CircularProgress />
            break;
        case ProfilePhotoState.NotSignedIn:
            profilePhotoComponent = <span>You must first sign in to edit your profile.</span>
    }

    return (
        <Grid
            container
            direction="column"
            spacing={3}
        >
            <Grid item>
                <Paper className={classes.profilePhotoPaper}>
                    <Grid
                        container
                        direction="column"
                        alignContent="center"
                        justifyContent="center"
                        spacing={3}
                    >
                        <Grid item>
                            { profilePhotoComponent }
                        </Grid>
                        <Grid item>
                            {avatarLoaded ? (
                                <Typography variant="h3" className={classes.userName}>
                                    {handle}
                                </Typography>
                            ) : (
                                <CircularProgress size={24} />
                            )}
                        </Grid>
                        <Grid item>
                            <Typography variant="body2">
                                <Link component={RouterLink} to={`/u/${authenticatedUser.username}`} >
                                    View public profile
                                </Link>
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
            <Grid item>
                <EditContactInfo
                    username={authenticatedUser.username}
                    contactInfo={contactInfo} 
                    profileLoaded={profileLoaded}
                />
            </Grid>
            <SetProfilePhotoModal 
                open={setProfilePhotoModal.isOpen && !!authenticatedUser.username}
                onClose={setProfilePhotoModal.close}
                onSetProfilePhotoId={(photoId: string) => setProfilePhotoId(photoId)}
                username={authenticatedUser.username}
            />
        </Grid>
    );
}

export default Profile;
